import { Injectable } from '@angular/core';
import { DialogModalSize } from 'src/modules/base/components/dialog/dialog-ref';
import { ManageFilterComponent } from 'src/modules/base/components/data-grid/manage-filter/manage-filter.component';

/**
 * FilterHelperService
 *
 * Provides context-agnostic static-style filter utilities for components
 * that manage their own saved-filter state without using DataGrid (e.g.
 * BedDashboardComponent, BedManagementComponent).
 *
 * Each method accepts a `ctx` object that must expose:
 *   - ctx.config.saveKey          (string)
 *   - ctx.preferenceService       (PreferenceService)
 *   - ctx.preferenceData          (any)
 *   - ctx.filterAr                (any[])
 *   - ctx.filterConfig            (any)
 *   - ctx.filterParam             (string)
 *   - ctx.filterFormGroup         (any)
 *   - ctx.filterFormMapping       (any)
 *   - ctx.kxFilter                (ElementRef / ViewChild)
 *   - ctx.spinner                 (LoaderService)
 *   - ctx.alert                   (AlertsService)
 *   - ctx.dialogService           (DialogService)
 *   - ctx.callAPI()               (method)
 */
@Injectable({ providedIn: 'root' })
export class FilterHelperService {

  // ─── Table Config ───────────────────────────────────────────────────────────

  getTableConfig(ctx: any, callback: any, force?: boolean) {
    const key = ctx.config.saveKey ?? ctx.config['saveKey'];
    ctx.preferenceService.getUserPreferenceConfig(key, (data) => {
      if (!force) ctx.preferenceData = data;
      callback(data);
    }, force);
  }

  // ─── Saved Filter ───────────────────────────────────────────────────────────

  getSavedFilter(ctx: any, callback?: Function) {
    this.getTableConfig(ctx, (data) => {
      ctx.filterAr = (data && data['filter__'] != undefined && data['filter__'].length)
        ? data['filter__'] : [];
      if (callback) callback(ctx.filterAr);
    });
  }

  // ─── Filter Condition ───────────────────────────────────────────────────────

  checkFilterCondition(filter: any): boolean {
    if (filter && filter.expression) {
      try { return eval(filter.expression); } catch (e) { return true; }
    }
    return true;
  }

  // ─── Apply Filter ───────────────────────────────────────────────────────────

  applyFilter(ctx: any, filter: any) {
    if (!filter || !filter.data) return;

    const hasActive = filter.name == ctx.filterConfig.FilterName;
    if (hasActive) {
      delete ctx.filterConfig.FilterName;
      delete ctx.filterConfig.currentFilter;
      delete ctx.filterConfig.currentFilterName;
      ctx.filterConfig.reset = new Date().getTime();
      ctx.filterConfig.quickReset = true;
      ctx.filterConfig.submit = new Date().getTime();
      ctx.filterConfig = { ...ctx.filterConfig };
      ctx.filterParam = '';
      if (ctx.kxFilter) ctx.kxFilter.resetParams();
      ctx.spinner.showSpinner();
      ctx.callAPI();
      return;
    }

    ctx.filterConfig.FilterName = filter.name;
    ctx.filterConfig.currentFilter = filter.key;
    ctx.filterConfig.currentFilterName = filter.name;

    if (ctx.filterFormMapping != undefined && ctx.filterFormGroup != undefined) {
      const oldFilterMapping = { ...ctx.filterFormMapping };
      ctx.filterConfig = { ...ctx.filterConfig };
      ctx.filterConfig.reset = new Date().getTime();
      ctx.filterConfig.currentFilter = filter.key;
      ctx.filterConfig.FilterName = filter.name;
      ctx.filterConfig.quickReset = false;

      if (ctx.kxFilter) ctx.kxFilter['stopChangeProp'] = true;
      setTimeout(async () => {
        for (const f in filter.data) {
          if (
            ctx.filterFormGroup &&
            ctx.filterFormGroup.controls &&
            ctx.filterFormGroup.controls[f] != undefined &&
            oldFilterMapping[f] != undefined
          ) {
            const valToSet = filter.data[f].value;
            if (ctx.filterFormGroup.controls[f].question_component) {
              ctx.filterFormGroup.controls[f].question_component.updateDependentValue(valToSet);
            }
            ctx.filterFormGroup.controls[f].setValue(valToSet);
          }
        }
        if (ctx.kxFilter) ctx.kxFilter['stopChangeProp'] = false;
        ctx.filterConfig = { ...ctx.filterConfig };
      });
    }

    ctx.filterParam = Object.entries(filter.data)
      .map(([k, v]: [string, any]) => v && v.value ? `${k}:${v.value}` : null)
      .filter(x => x != null)
      .join(',');

    ctx.spinner.showSpinner();
    ctx.callAPI();
  }

  // ─── Save Filter Data ───────────────────────────────────────────────────────

  saveFilterData(ctx: any, obj: any) {
    this.getTableConfig(ctx, (all_resource_data) => {
      const saveData: any = (all_resource_data) ? all_resource_data : {};
      saveData['filter__'] = (saveData['filter__'] == undefined) ? [] : saveData['filter__'];

      let found = false;
      let index = 0;
      if (obj.key != undefined) {
        for (index = 0; index < saveData['filter__'].length; index++) {
          if (saveData['filter__'][index].key == obj.key) { found = true; break; }
        }
      }

      if (found) saveData['filter__'][index] = obj;
      else saveData['filter__'].push(obj);

      this.saveTableConfigData(ctx, saveData, () => {
        ctx.alert.success('Request Successfull.');
        this.getSavedFilter(ctx);
      });
    });
  }

  // ─── Save Table Config Data ─────────────────────────────────────────────────

  saveTableConfigData(ctx: any, data: any, callback: any = false, userScope?: boolean, del?: string) {
    let key = ctx.config.saveKey ?? ctx.config['saveKey'];
    const data__ = JSON.parse(JSON.stringify(data));
    if (userScope) {
      this.getTableConfig(ctx, (all_resource_data) => {
        let account_data: any = {};
        if (all_resource_data['account__' + key] != undefined) {
          account_data = all_resource_data['account__' + key];
        }
        if (account_data.filter__ != undefined && account_data.filter__.length > 0) {
          data__.filter__ = del
            ? JSON.parse(JSON.stringify(account_data.filter__))
            : [...data__.filter__, ...account_data.filter__];
        }
        if (data__.filter__ != undefined && data__.filter__.length > 0) {
          data__.filter__ = data__.filter__.filter((x: any) => x.userGroupId != undefined && x.userGroupId);
        }
        if (del) {
          const delIndex = data__.filter__.findIndex((x: any) => x.key != undefined && x.key == del);
          if (delIndex > -1) data__.filter__.splice(delIndex, 1);
          else { ctx.alert.error('ERROR'); console.error('Deleted Filter key not found'); }
        }
        key = 'account__' + key;
        account_data['filter__'] = data__.filter__;
        ctx.preferenceService.saveConfigurationData({ key: key }, account_data, () => {
          if (callback && typeof callback == 'function') callback();
        });
      }, true);
    } else {
      ctx.preferenceService.saveTableConfigData(key, data, callback);
    }
  }

  // ─── Manage Filter ──────────────────────────────────────────────────────────

  manageFilter(ctx: any) {
    const key = ctx.config.saveKey ?? ctx.config['saveKey'];
    if (key == undefined) { console.error('saveKey not found'); return; }
    this.getSavedFilter(ctx, (filterData: any) => {
      const popobj = { data: { self: ctx, data: filterData }, title: 'Manage Your Filters', size: DialogModalSize.Sm };
      ctx.dialogService.open(ManageFilterComponent, popobj).afterClosed.subscribe(() => {
        this.getSavedFilter(ctx);
      });
    });
  }

}
